import { MONKEY_USER_ID } from '../../../constants';

/**
 * Calculate time to start for a match
 */
export const calculateTimeToStart = (
  matchDate: string | Date,
  matchStatus?: string
): string => {
  const date = new Date(matchDate);
  const now = new Date();

  if (matchStatus === 'FINISHED') {
    return 'Finished';
  } else if (matchStatus === 'LIVE') {
    const elapsedMs = now.getTime() - date.getTime();
    const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));
    if (elapsedMinutes >= 150) {
      return '90+60';
    }
    return `${elapsedMinutes}'`;
  } else {
    const diffMs = date.getTime() - now.getTime();
    if (diffMs <= 0) {
      return 'Starting soon';
    }
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      return remainingHours > 0
        ? `${diffDays}d ${remainingHours}h`
        : `${diffDays}d`;
    } else if (diffHours > 0) {
      return diffMinutes > 0
        ? `${diffHours}h ${diffMinutes}m`
        : `${diffHours}h`;
    } else {
      return `${diffMinutes}m`;
    }
  }
};

/**
 * Get user display name from challenge-embedded participant maps.
 * @param id - User ID (userId or email)
 * @param participantDisplayNamesById - Map of id/email -> display name (from challenge response; single source of truth)
 * @param participantEmailsById - Optional map of userId -> email (to resolve email -> userId -> name)
 */
export const getUserName = (
  id: string,
  participantDisplayNamesById?: Record<string, string>,
  participantEmailsById?: Record<string, string>,
): string => {
  const fromChallenge = participantDisplayNamesById?.[id] || participantDisplayNamesById?.[id?.toLowerCase?.()?.trim?.()];
  if (fromChallenge && fromChallenge.trim() && !fromChallenge.includes('@')) {
    return fromChallenge.trim();
  }

  if (id === MONKEY_USER_ID) {
    return 'Monkey';
  }

  if (id.endsWith('0000')) {
    return 'cryptorider111';
  }

  // When id is email: try resolving via participantEmailsById -> participantDisplayNamesById
  if (id.includes('@') && participantEmailsById && participantDisplayNamesById) {
    const normalizedId = id.toLowerCase().trim();
    for (const [userId, email] of Object.entries(participantEmailsById)) {
      if (email?.toLowerCase?.()?.trim?.() === normalizedId) {
        const name = participantDisplayNamesById[userId];
        if (name && !name.includes('@')) return name;
        break;
      }
    }
  }

  if (id.includes('@')) {
    const emailPrefix = id.split('@')[0];
    if (emailPrefix && emailPrefix !== 'user' && !emailPrefix.includes('placeholder')) {
      return emailPrefix;
    }
  }

  // For invited_ users, try to get email from participantEmailsById and resolve to name
  if (id.startsWith('invited_') && participantEmailsById?.[id]) {
    const email = participantEmailsById[id];
    if (email && !email.includes('@placeholder.com')) {
      if (participantDisplayNamesById) {
        for (const [userId, e] of Object.entries(participantEmailsById)) {
          if (e === email && participantDisplayNamesById[userId]) return participantDisplayNamesById[userId];
        }
      }
      const emailPrefix = email.split('@')[0];
      if (emailPrefix) return emailPrefix;
    }
  }

  return `User ${id.slice(-4)}`;
};

